import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Card, Button, Modal, Form, Input, InputNumber, Select,
  DatePicker, Space, Tag, Typography, Statistic, Row, Col, message,
  Popconfirm, Tooltip, Badge, Switch, Drawer, Descriptions, List, Divider
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined,
  ShopOutlined, WarningOutlined, SearchOutlined, GlobalOutlined,
  MailOutlined, PhoneOutlined, CalendarOutlined, TagsOutlined,
  FileTextOutlined, DownloadOutlined, FilePdfOutlined, FileWordOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { clientVendorService, vendorService, contractService, userService } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ClientVendors = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [clientVendors, setClientVendors] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [stats, setStats] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showExpiringOnly, setShowExpiringOnly] = useState(searchParams.get('filter') === 'expiring');
  const [showMyVendorsOnly, setShowMyVendorsOnly] = useState(searchParams.get('filter') === 'mine');
  const [form] = Form.useForm();

  // Drawer state for vendor details
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [vendorContracts, setVendorContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [users, setUsers] = useState([]);

  // Filter vendors based on search query and expiring filter
  const filteredVendors = useMemo(() => {
    let result = clientVendors;

    // Apply expiring filter first
    if (showExpiringOnly) {
      result = result.filter(cv => {
        if (!cv.contract_end_date) return false;
        const daysUntil = dayjs(cv.contract_end_date).diff(dayjs(), 'day');
        return daysUntil >= 0 && daysUntil <= 90;
      });
    }

    // Then apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(cv =>
        cv.vendor?.name?.toLowerCase().includes(query) ||
        cv.status?.toLowerCase().includes(query) ||
        cv.contact_name?.toLowerCase().includes(query) ||
        cv.notes?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [clientVendors, searchQuery, showExpiringOnly]);

  // Clear URL param when filter is toggled off
  const handleExpiringFilterChange = (checked) => {
    setShowExpiringOnly(checked);
    if (checked) {
      setSearchParams({ filter: 'expiring' });
    } else {
      setSearchParams({});
    }
  };

  // Open vendor detail drawer
  const handleVendorClick = async (record) => {
    setSelectedVendor(record);
    setDrawerVisible(true);
    setLoadingProducts(true);
    setLoadingContracts(true);

    // Fetch products
    try {
      const products = await clientVendorService.getProducts(record.id);
      setVendorProducts(products);
    } catch (error) {
      console.error('Failed to load vendor products:', error);
      setVendorProducts([]);
    } finally {
      setLoadingProducts(false);
    }

    // Fetch contracts
    try {
      const contracts = await contractService.getAll({ client_vendor_id: record.id });
      setVendorContracts(contracts);
    } catch (error) {
      console.error('Failed to load vendor contracts:', error);
      setVendorContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedVendor(null);
    setVendorProducts([]);
    setVendorContracts([]);
  };

  // Handle contract download
  const handleContractDownload = (contract) => {
    const url = contractService.getDownloadUrl(contract.id);
    window.open(url, '_blank');
  };

  // Get file icon for contract
  const getContractFileIcon = (fileType) => {
    if (fileType === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    if (['doc', 'docx'].includes(fileType)) return <FileWordOutlined style={{ color: '#1890ff' }} />;
    return <FileTextOutlined />;
  };

  // Helper to calculate contract status
  const getContractStatus = (endDate) => {
    if (!endDate) return null;
    const daysUntil = dayjs(endDate).diff(dayjs(), 'day');
    if (daysUntil < 0) return { text: `Expired ${Math.abs(daysUntil)} days ago`, color: 'red' };
    if (daysUntil <= 30) return { text: `Expires in ${daysUntil} days`, color: 'red' };
    if (daysUntil <= 90) return { text: `Expires in ${daysUntil} days`, color: 'orange' };
    return { text: `${daysUntil} days remaining`, color: 'green' };
  };

  const fetchData = async (myVendorsFilter = showMyVendorsOnly) => {
    try {
      setLoading(true);
      const filters = myVendorsFilter ? { my_vendors_only: true } : {};
      const [vendorsData, statsData, allVendors, usersData] = await Promise.all([
        clientVendorService.getAll(filters),
        clientVendorService.getStats(),
        vendorService.getAll(),
        userService.getAll()
      ]);
      setClientVendors(vendorsData);
      setStats(statsData);
      setUsers(usersData);

      // Filter out vendors already added
      const addedVendorIds = vendorsData.map(cv => cv.vendor_id);
      const available = allVendors.filter(v => !addedVendorIds.includes(v.id));
      setAvailableVendors(available);
    } catch (error) {
      message.error('Failed to load vendor data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle My Vendors filter change
  const handleMyVendorsFilterChange = (checked) => {
    setShowMyVendorsOnly(checked);
    if (checked) {
      setShowExpiringOnly(false);  // Can't combine filters for now
      setSearchParams({ filter: 'mine' });
    } else {
      setSearchParams({});
    }
    fetchData(checked);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAddModal = () => {
    setEditingVendor(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingVendor(record);
    form.setFieldsValue({
      ...record,
      contract_start_date: record.contract_start_date ? dayjs(record.contract_start_date) : null,
      contract_end_date: record.contract_end_date ? dayjs(record.contract_end_date) : null,
      business_owner_id: record.business_owner_id,
      contract_owner_id: record.contract_owner_id,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingVendor(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        contract_start_date: values.contract_start_date?.toISOString(),
        contract_end_date: values.contract_end_date?.toISOString(),
      };

      if (editingVendor) {
        await clientVendorService.update(editingVendor.id, data);
        message.success('Vendor updated successfully');
      } else {
        await clientVendorService.create(data);
        message.success('Vendor added successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleRemove = async (id) => {
    try {
      await clientVendorService.remove(id);
      message.success('Vendor removed successfully');
      fetchData();
    } catch (error) {
      message.error('Failed to remove vendor');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      inactive: 'default',
      pending: 'orange',
      churned: 'red'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Vendor',
      dataIndex: ['vendor', 'name'],
      key: 'vendor_name',
      sorter: (a, b) => a.vendor.name.localeCompare(b.vendor.name),
    },
    {
      title: 'Business Owner',
      dataIndex: ['business_owner', 'full_name'],
      key: 'business_owner',
      render: (name, record) => {
        if (!record.business_owner) return <span style={{ color: '#8c8c8c' }}>Unassigned</span>;
        return (
          <Tooltip title={record.business_owner.email}>
            <span>{name || record.business_owner.email}</span>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const nameA = a.business_owner?.full_name || '';
        const nameB = b.business_owner?.full_name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Contract Owner',
      dataIndex: ['contract_owner', 'full_name'],
      key: 'contract_owner',
      render: (name, record) => {
        if (!record.contract_owner) return <span style={{ color: '#8c8c8c' }}>Unassigned</span>;
        return (
          <Tooltip title={record.contract_owner.email}>
            <span>{name || record.contract_owner.email}</span>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const nameA = a.contract_owner?.full_name || '';
        const nameB = b.contract_owner?.full_name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Annual Spend',
      dataIndex: 'annual_spend',
      key: 'annual_spend',
      render: (value) => `$${(value || 0).toLocaleString()}`,
      sorter: (a, b) => (a.annual_spend || 0) - (b.annual_spend || 0),
    },
    {
      title: 'Contract End',
      dataIndex: 'contract_end_date',
      key: 'contract_end_date',
      render: (date) => {
        if (!date) return '-';
        const endDate = dayjs(date);
        const daysUntil = endDate.diff(dayjs(), 'day');
        const isExpiringSoon = daysUntil <= 90 && daysUntil > 0;
        const isExpired = daysUntil < 0;

        return (
          <Space>
            {endDate.format('MMM D, YYYY')}
            {isExpiringSoon && (
              <Tooltip title={`Expires in ${daysUntil} days`}>
                <Badge status="warning" />
              </Tooltip>
            )}
            {isExpired && (
              <Tooltip title="Contract expired">
                <Badge status="error" />
              </Tooltip>
            )}
          </Space>
        );
      },
      sorter: (a, b) => {
        if (!a.contract_end_date) return 1;
        if (!b.contract_end_date) return -1;
        return dayjs(a.contract_end_date).unix() - dayjs(b.contract_end_date).unix();
      },
    },
    {
      title: 'Payment Terms',
      dataIndex: 'payment_terms',
      key: 'payment_terms',
      render: (terms) => terms || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Pending', value: 'pending' },
        { text: 'Churned', value: 'churned' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Remove this vendor?"
            description="The vendor will be marked as churned."
            onConfirm={() => handleRemove(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Remove">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>My Vendors</Title>
      <p>Manage your organization's vendor relationships</p>

      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Active Vendors"
                value={stats.active_vendors}
                prefix={<ShopOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Annual Spend"
                value={stats.total_annual_spend}
                prefix={<DollarOutlined />}
                precision={0}
                valueStyle={{ color: '#0050b3' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Contract Value"
                value={stats.total_contract_value}
                prefix={<DollarOutlined />}
                precision={0}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              hoverable={stats.contracts_expiring_soon > 0}
              style={stats.contracts_expiring_soon > 0 ? { cursor: 'pointer' } : {}}
              onClick={() => {
                if (stats.contracts_expiring_soon > 0) {
                  handleExpiringFilterChange(!showExpiringOnly);
                }
              }}
            >
              <Statistic
                title="Expiring Soon"
                value={stats.contracts_expiring_soon}
                prefix={<WarningOutlined />}
                valueStyle={{ color: stats.contracts_expiring_soon > 0 ? '#cf1322' : '#8c8c8c' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Vendors Table */}
      <Card
        title="Vendor Portfolio"
        extra={
          <Space>
            <Space size="small">
              <Switch
                checked={showMyVendorsOnly}
                onChange={handleMyVendorsFilterChange}
                size="small"
              />
              <span style={{ color: showMyVendorsOnly ? '#1890ff' : '#8c8c8c', fontSize: 13 }}>
                My Vendors
              </span>
            </Space>
            <Divider type="vertical" />
            <Space size="small">
              <Switch
                checked={showExpiringOnly}
                onChange={handleExpiringFilterChange}
                size="small"
                disabled={showMyVendorsOnly}
              />
              <span style={{ color: showExpiringOnly ? '#faad14' : '#8c8c8c', fontSize: 13 }}>
                Expiring soon
              </span>
            </Space>
            <Input
              placeholder="Search vendors..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ width: 250 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
              Add Vendor
            </Button>
          </Space>
        }
      >
        {(searchQuery || showExpiringOnly || showMyVendorsOnly) && (
          <div style={{ marginBottom: 16, color: '#8c8c8c' }}>
            Showing {filteredVendors.length} of {showMyVendorsOnly ? 'your' : clientVendors.length} vendors
            {showMyVendorsOnly && <Tag color="blue" style={{ marginLeft: 8 }}>My Vendors</Tag>}
            {showExpiringOnly && <Tag color="warning" style={{ marginLeft: 8 }}>Expiring within 90 days</Tag>}
          </div>
        )}
        <Table
          columns={columns}
          dataSource={filteredVendors}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          onRow={(record) => ({
            onClick: () => handleVendorClick(record),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingVendor ? 'Edit Vendor' : 'Add Vendor'}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: 'active', renewal_reminder_days: 30 }}
        >
          {!editingVendor && (
            <Form.Item
              name="vendor_id"
              label="Vendor"
              rules={[{ required: true, message: 'Please select a vendor' }]}
            >
              <Select
                showSearch
                placeholder="Select a vendor"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {availableVendors.map(vendor => (
                  <Option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="annual_spend" label="Annual Spend ($)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="monthly_spend" label="Monthly Spend ($)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contract_start_date" label="Contract Start">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contract_end_date" label="Contract End">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contract_value" label="Contract Value ($)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payment_terms" label="Payment Terms">
                <Select placeholder="Select payment terms">
                  <Option value="Net 15">Net 15</Option>
                  <Option value="Net 30">Net 30</Option>
                  <Option value="Net 45">Net 45</Option>
                  <Option value="Net 60">Net 60</Option>
                  <Option value="Net 90">Net 90</Option>
                  <Option value="Annual">Annual</Option>
                  <Option value="Monthly">Monthly</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="pending">Pending</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="renewal_reminder_days" label="Reminder (days before expiry)">
                <InputNumber style={{ width: '100%' }} min={0} max={365} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contact_name" label="Contact Name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_email" label="Contact Email">
                <Input type="email" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="business_owner_id" label="Business Owner">
                <Select
                  allowClear
                  placeholder="Select business owner"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contract_owner_id" label="Contract Owner">
                <Select
                  allowClear
                  placeholder="Select contract owner"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingVendor ? 'Update' : 'Add Vendor'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Vendor Detail Drawer */}
      <Drawer
        title={
          <Space>
            <span style={{ fontSize: 18, fontWeight: 600 }}>{selectedVendor?.vendor?.name}</span>
            {selectedVendor?.vendor?.primary_category && (
              <Tag color="blue">{selectedVendor.vendor.primary_category}</Tag>
            )}
            <Tag color={getStatusColor(selectedVendor?.status)}>
              {selectedVendor?.status?.toUpperCase()}
            </Tag>
          </Space>
        }
        placement="right"
        width={520}
        onClose={closeDrawer}
        open={drawerVisible}
        extra={
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                closeDrawer();
                showEditModal(selectedVendor);
              }}
            >
              Edit
            </Button>
          </Space>
        }
      >
        {selectedVendor && (
          <>
            {/* Website Link */}
            {selectedVendor.vendor?.website && (
              <div style={{ marginBottom: 16 }}>
                <a
                  href={selectedVendor.vendor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GlobalOutlined style={{ marginRight: 8 }} />
                  {selectedVendor.vendor.website}
                </a>
              </div>
            )}

            {/* Financial Overview */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Annual Spend"
                  value={selectedVendor.annual_spend || 0}
                  prefix="$"
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Monthly Spend"
                  value={selectedVendor.monthly_spend || 0}
                  prefix="$"
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Contract Value"
                  value={selectedVendor.contract_value || 0}
                  prefix="$"
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
            </Row>

            <Divider />

            {/* Contract Details */}
            <Descriptions title="Contract Details" column={1} size="small">
              <Descriptions.Item label="Contract Period">
                {selectedVendor.contract_start_date
                  ? dayjs(selectedVendor.contract_start_date).format('MMM D, YYYY')
                  : '-'}{' '}
                to{' '}
                {selectedVendor.contract_end_date
                  ? dayjs(selectedVendor.contract_end_date).format('MMM D, YYYY')
                  : '-'}
              </Descriptions.Item>
              {selectedVendor.contract_end_date && (
                <Descriptions.Item label="Status">
                  {(() => {
                    const status = getContractStatus(selectedVendor.contract_end_date);
                    return status ? (
                      <Tag color={status.color}>{status.text}</Tag>
                    ) : '-';
                  })()}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Payment Terms">
                {selectedVendor.payment_terms || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Auto-Renew">
                {selectedVendor.auto_renew ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="default">No</Tag>
                )}
              </Descriptions.Item>
              {selectedVendor.renewal_reminder_days > 0 && (
                <Descriptions.Item label="Renewal Reminder">
                  {selectedVendor.renewal_reminder_days} days before expiry
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            {/* Contact Information */}
            <Descriptions title="Contact Information" column={1} size="small">
              <Descriptions.Item label="Name">
                {selectedVendor.contact_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedVendor.contact_email ? (
                  <a href={`mailto:${selectedVendor.contact_email}`}>
                    <MailOutlined style={{ marginRight: 4 }} />
                    {selectedVendor.contact_email}
                  </a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedVendor.contact_phone ? (
                  <a href={`tel:${selectedVendor.contact_phone}`}>
                    <PhoneOutlined style={{ marginRight: 4 }} />
                    {selectedVendor.contact_phone}
                  </a>
                ) : '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Ownership */}
            <Descriptions title="Ownership" column={1} size="small">
              <Descriptions.Item label="Business Owner">
                {selectedVendor.business_owner ? (
                  <Tooltip title={selectedVendor.business_owner.email}>
                    <span>{selectedVendor.business_owner.full_name || selectedVendor.business_owner.email}</span>
                  </Tooltip>
                ) : <span style={{ color: '#8c8c8c' }}>Unassigned</span>}
              </Descriptions.Item>
              <Descriptions.Item label="Contract Owner">
                {selectedVendor.contract_owner ? (
                  <Tooltip title={selectedVendor.contract_owner.email}>
                    <span>{selectedVendor.contract_owner.full_name || selectedVendor.contract_owner.email}</span>
                  </Tooltip>
                ) : <span style={{ color: '#8c8c8c' }}>Unassigned</span>}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Products */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={5} style={{ margin: 0 }}>
                  Products ({vendorProducts.length})
                </Title>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate(`/product-database?vendor=${selectedVendor.vendor?.name}`)}
                >
                  View All
                </Button>
              </div>
              <List
                size="small"
                loading={loadingProducts}
                dataSource={vendorProducts.slice(0, 8)}
                locale={{ emptyText: 'No products found' }}
                renderItem={(product) => (
                  <List.Item>
                    <Space>
                      <span>{product.name}</span>
                      {product.category && (
                        <Tag color="default" style={{ fontSize: 11 }}>
                          {product.category}
                        </Tag>
                      )}
                    </Space>
                  </List.Item>
                )}
              />
              {vendorProducts.length > 8 && (
                <div style={{ textAlign: 'center', marginTop: 8, color: '#8c8c8c' }}>
                  +{vendorProducts.length - 8} more products
                </div>
              )}
            </div>

            <Divider />

            {/* Contracts */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={5} style={{ margin: 0 }}>
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  Contracts ({vendorContracts.length})
                </Title>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate('/contracts')}
                >
                  Manage All
                </Button>
              </div>
              <List
                size="small"
                loading={loadingContracts}
                dataSource={vendorContracts.slice(0, 5)}
                locale={{ emptyText: 'No contracts found for this vendor' }}
                renderItem={(contract) => (
                  <List.Item
                    actions={
                      contract.file_path ? [
                        <Tooltip title="Download">
                          <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleContractDownload(contract)}
                          />
                        </Tooltip>
                      ] : []
                    }
                  >
                    <List.Item.Meta
                      avatar={contract.file_type ? getContractFileIcon(contract.file_type) : <FileTextOutlined />}
                      title={
                        <Space>
                          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/contracts')}>
                            {contract.title}
                          </span>
                          {contract.contract_type && (
                            <Tag color="blue" style={{ fontSize: 10 }}>
                              {contract.contract_type.toUpperCase()}
                            </Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space size="small">
                          {contract.total_value && (
                            <span>${contract.total_value.toLocaleString()}</span>
                          )}
                          {contract.end_date && (
                            <span>
                              Expires: {dayjs(contract.end_date).format('MMM D, YYYY')}
                              {(() => {
                                const status = getContractStatus(contract.end_date);
                                return status && status.color !== 'green' ? (
                                  <Tag color={status.color} style={{ marginLeft: 4, fontSize: 10 }}>
                                    {status.text}
                                  </Tag>
                                ) : null;
                              })()}
                            </span>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
              {vendorContracts.length > 5 && (
                <div style={{ textAlign: 'center', marginTop: 8, color: '#8c8c8c' }}>
                  +{vendorContracts.length - 5} more contracts
                </div>
              )}
              {vendorContracts.length === 0 && !loadingContracts && (
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/contracts')}
                  style={{ marginTop: 8 }}
                >
                  Add Contract
                </Button>
              )}
            </div>

            {/* Tags */}
            {selectedVendor.tags && selectedVendor.tags.length > 0 && (
              <>
                <Divider />
                <div>
                  <Title level={5} style={{ marginBottom: 12 }}>
                    <TagsOutlined style={{ marginRight: 8 }} />
                    Tags
                  </Title>
                  <Space wrap>
                    {selectedVendor.tags.map((tag, index) => (
                      <Tag key={index}>{tag}</Tag>
                    ))}
                  </Space>
                </div>
              </>
            )}

            {/* Notes */}
            {selectedVendor.notes && (
              <>
                <Divider />
                <div>
                  <Title level={5} style={{ marginBottom: 12 }}>Notes</Title>
                  <div style={{
                    background: '#fafafa',
                    padding: 12,
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedVendor.notes}
                  </div>
                </div>
              </>
            )}

            {/* Timestamps */}
            <Divider />
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
              <div>Added: {dayjs(selectedVendor.created_at).format('MMM D, YYYY')}</div>
              <div>Last updated: {dayjs(selectedVendor.updated_at).format('MMM D, YYYY h:mm A')}</div>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ClientVendors;
